<template>
  <div class="setup-menu">
    <v-menu
      :model-value="menuOpen"
      @update:model-value="updateMenu"
      open-on-hover
      :open-delay="120"
      :close-delay="220"
      location="bottom"
      offset="8"
      transition="fade-transition"
      content-class="setup-menu-overlay"
    >
      <!-- ============================================= -->
      <!-- ACTIVATOR -->
      <!-- ============================================= -->
      <template #activator="{ props }">
        <v-btn
          v-bind="props"
          variant="text"
          class="setup-menu-button text-none"
          :style="{
            color:
              theme.appBarColor ||
              theme.text
          }"
        >
          <v-icon
            start
            size="20"
          >
            mdi-cog-outline
          </v-icon>

          {{
            lang === "en"
              ? "Setup"
              : "Configurer"
          }}

          <v-icon
            end
            size="18"
          >
            mdi-chevron-down
          </v-icon>
        </v-btn>
      </template>

      <!-- ============================================= -->
      <!-- SETUP CARD -->
      <!-- ============================================= -->
      <v-card
        elevation="10"
        class="setup-menu-card"
        :style="{
          background:
            theme.cardBodyBg ||
            theme.surface,
          color:
            theme.cardBodyColor ||
            theme.text
        }"
      >
        <!-- =========================================== -->
        <!-- HEADER -->
        <!-- =========================================== -->
        <div class="setup-header">
          <div class="setup-header-icon">
            <v-icon size="26">
              mdi-tune-variant
            </v-icon>
          </div>

          <div>
            <div class="setup-header-title">
              {{
                lang === "en"
                  ? "Business Setup"
                  : "Configuration de l’entreprise"
              }}
            </div>

            <div class="setup-header-description">
              {{
                lang === "en"
                  ? "Manage shared reference data used across ERP modules."
                  : "Gérez les données de référence partagées entre les modules ERP."
              }}
            </div>
          </div>
        </div>

        <v-divider />

        <!-- =========================================== -->
        <!-- SETUP ITEMS -->
        <!-- =========================================== -->
        <div class="setup-items">
          <router-link
            v-for="item in visibleBusiness"
            :key="item.key"
            :to="item.to"
            class="setup-item"
            @click="closeMenu"
          >
            <div
              class="setup-item-icon"
              :style="{
                background:
                  theme.sectionBgColor ||
                  'rgba(127, 127, 127, 0.10)',

                color:
                  theme.appBarBg ||
                  theme.cardBodyColor ||
                  theme.text
              }"
            >
              <v-icon
                :icon="item.icon"
                size="23"
              />
            </div>

            <div class="setup-item-content">
              <div class="setup-item-title">
                {{ item.title }}
              </div>

              <div class="setup-item-description">
                {{ item.desc }}
              </div>
            </div>

            <v-icon
              size="18"
              class="setup-item-arrow"
            >
              mdi-chevron-right
            </v-icon>
          </router-link>

          <div
            v-if="!visibleBusiness.length"
            class="setup-empty"
          >
            <v-icon
              size="28"
              class="mb-2"
            >
              mdi-lock-outline
            </v-icon>

            <div>
              {{
                lang === "en"
                  ? "No setup options are available for your current permissions."
                  : "Aucune option de configuration n’est disponible avec vos autorisations actuelles."
              }}
            </div>
          </div>
        </div>

        <!-- =========================================== -->
        <!-- FOOTER -->
        <!-- =========================================== -->
        <div class="setup-footer">
          <v-icon size="18">
            mdi-information-outline
          </v-icon>

          <span>
            {{
              lang === "en"
                ? "Changes made here may be used by multiple business modules."
                : "Les modifications effectuées ici peuvent être utilisées par plusieurs modules."
            }}
          </span>
        </div>
      </v-card>
    </v-menu>
  </div>

  <v-spacer />
</template>

<script setup>
import {
  computed,
  ref,
  watch
} from "vue";

import {
  useI18n
} from "vue-i18n";

import {
  useRouter
} from "vue-router";

import {
  useLangStore
} from "@/store/lang";

import {
  useAuthStore,
  useThemeStore
} from "@/store/auth";

// ============================================================
// STORES
// ============================================================

const {
  locale
} = useI18n();

const router =
  useRouter();

const langStore =
  useLangStore();

const themeStore =
  useThemeStore();

const auth =
  useAuthStore();

// ============================================================
// LANGUAGE
// ============================================================

const lang =
  computed(
    () =>
      langStore.currentLanguage ||
      "en"
  );

watch(
  lang,
  newLang => {
    locale.value =
      newLang || "en";
  },
  {
    immediate: true
  }
);

// ============================================================
// THEME
// ============================================================

const theme =
  computed(
    () =>
      themeStore.themeStyles ||
      {}
  );

// ============================================================
// MENU STATE
// ============================================================

const menuOpen =
  ref(false);

function updateMenu(
  value
) {
  menuOpen.value =
    Boolean(value);
}

function closeMenu() {
  menuOpen.value =
    false;
}

// ============================================================
// ROUTE RESOLVER
// ============================================================

function resolveRoute(
  englishRoute,
  frenchRoute
) {
  const routeName =
    lang.value === "en"
      ? englishRoute
      : frenchRoute;

  try {
    return router.resolve({
      name:
        routeName
    }).href;
  }
  catch (error) {
    console.error(
      `Unable to resolve route "${routeName}":`,
      error
    );

    return "/";
  }
}

// ============================================================
// BUSINESS SETUP ITEMS
// ============================================================

const business =
  computed(() => [
    {
      key:
        "operations",

      title:
        lang.value === "en"
          ? "Operation"
          : "Opération",

      desc:
        lang.value === "en"
          ? "Configure major ERP operations"
          : "Configurer les principales opérations ERP",

      icon:
        "mdi-cogs",

      to:
        resolveRoute(
          "OperationsEN",
          "OperationsFR"
        )
    },

    {
      key:
        "project",

      title:
        lang.value === "en"
          ? "Project"
          : "Projet",

      desc:
        lang.value === "en"
          ? "Configure project codes and references"
          : "Configurer les codes et références de projet",

      icon:
        "mdi-folder-cog-outline",

      to:
        resolveRoute(
          "ProjectCodeEN",
          "ProjectCodeFR"
        )
    },

    {
      key:
        "business-partners",

      title:
        lang.value === "en"
          ? "Business Partners"
          : "Partenaires d’affaires",

      desc:
        lang.value === "en"
          ? "Configure customers, suppliers and partners"
          : "Configurer les clients, fournisseurs et partenaires",

      icon:
        "mdi-handshake-outline",

      to:
        resolveRoute(
          "BusinessPartnersEN",
          "BusinessPartnersFR"
        )
    },

    {
      key:
        "cost-center",

      title:
        lang.value === "en"
          ? "Cost Center"
          : "Centre de coûts",

      desc:
        lang.value === "en"
          ? "Configure expense and cost groupings"
          : "Configurer les regroupements de coûts et dépenses",

      icon:
        "mdi-target",

      to:
        resolveRoute(
          "CostCenterEN",
          "CostCenterFR"
        )
    },

    {
      key:
        "department",

      title:
        lang.value === "en"
          ? "Department"
          : "Département",

      desc:
        lang.value === "en"
          ? "Configure organizational departments"
          : "Configurer les départements de l’organisation",

      icon:
        "mdi-office-building-outline",

      to:
        resolveRoute(
          "DepartmentsEN",
          "DepartmentsFR"
        )
    }
  ]);

// ============================================================
// PERMISSION FILTERING
// ============================================================

const visibleBusiness =
  computed(() =>
    business.value.filter(
      item => {
        if (
          !item.permission
        ) {
          return true;
        }

        return auth.hasPermission(
          item.permission
        );
      }
    )
  );
</script>

<style scoped>
/* ============================================================
   ROOT
============================================================ */

.setup-menu {
  display: flex;
  align-items: center;
}

/* ============================================================
   ACTIVATOR
============================================================ */

.setup-menu-button {
  min-height: 44px;
  margin-top: 20px;
  padding-right: 10px;
  padding-left: 10px;
  font-size: 16px;
  font-weight: 400;
}

/* ============================================================
   CARD
============================================================ */

.setup-menu-card {
  width:
    min(
      440px,
      92vw
    );

  overflow: hidden;
  border-radius: 14px;
}

/* ============================================================
   HEADER
============================================================ */

.setup-header {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 20px 22px;
}

.setup-header-icon {
  display: flex;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
  align-items: center;
  justify-content: center;
  border-radius: 11px;

  background:
    rgba(
      127,
      127,
      127,
      0.1
    );
}

.setup-header-title {
  font-size: 17px;
  font-weight: 700;
}

.setup-header-description {
  margin-top: 3px;
  font-size: 10.5px;
  line-height: 1.45;
  opacity: 0.62;
}

/* ============================================================
   ITEMS
============================================================ */

.setup-items {
  display: flex;
  flex-direction: column;
  padding: 9px;
}

.setup-item {
  display: flex;
  width: 100%;
  min-height: 70px;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 9px;
  color: inherit;
  text-decoration: none;

  transition:
    background-color 0.15s ease,
    transform 0.15s ease;
}

.setup-item:hover {
  background:
    rgba(
      127,
      127,
      127,
      0.09
    );

  transform:
    translateX(2px);
}

/* ============================================================
   ITEM ICON
============================================================ */

.setup-item-icon {
  display: flex;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
}

/* ============================================================
   ITEM CONTENT
============================================================ */

.setup-item-content {
  min-width: 0;
  flex: 1;
}

.setup-item-title {
  margin-bottom: 3px;
  font-size: 13px;
  font-weight: 650;
}

.setup-item-description {
  font-size: 10.5px;
  line-height: 1.45;
  opacity: 0.62;
}

/* ============================================================
   ARROW
============================================================ */

.setup-item-arrow {
  flex: 0 0 auto;
  opacity: 0.35;

  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.setup-item:hover
.setup-item-arrow {
  opacity: 1;
  transform: translateX(2px);
}

/* ============================================================
   EMPTY
============================================================ */

.setup-empty {
  padding: 28px 20px;
  text-align: center;
  font-size: 11px;
  line-height: 1.5;
  opacity: 0.6;
}

/* ============================================================
   FOOTER
============================================================ */

.setup-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;

  border-top:
    1px solid
    rgba(
      127,
      127,
      127,
      0.15
    );

  font-size: 9.5px;
  line-height: 1.4;
  opacity: 0.62;
}

/* ============================================================
   MOBILE
============================================================ */

@media (max-width: 599px) {
  .setup-menu-card {
    width:
      min(
        360px,
        94vw
      );

    max-height: 75vh;
    overflow-y: auto;
  }

  .setup-header {
    padding: 17px;
  }

  .setup-item {
    min-height: 64px;
    padding: 9px 10px;
  }
}
</style>